<template>
    <div class="email-consult-editor" @dragover.prevent="handleDragOver">
        <div class="email-consult-editor__wrapper">
            <CompassAvatar
                size="small"
                :initials="currentUserInitials"
                :avatarColorType="avatarColor(currentUserFullName)"
            />
            <div class="email-consult-editor__panel">
                <div class="email-consult-editor__panel-header">
                    <EmailRecipientsInputs
                        :hashPanel="hashPanel"
                        :recipients="recipients"
                        class="email-recipients-inputs"
                        @updateAddressesEmails="onUpdateAddressesEmails"
                    />
                </div>
                <div class="email-consult-editor__panel-subject">
                    <EmailSubjectInput
                        :subject="email.subject"
                        @update:subject="
                            (newSubject) => {
                                email.subject = newSubject;
                            }
                        "
                    />
                </div>
                <EmailRelatedObject v-if="isLinkedObjectExist" :object="thread[0].linked_object" />

                <RichText
                    ref="refEmailPanelRichText"
                    v-model="email.content"
                    :options="optionsRichText"
                    :isCustom="true"
                    :showRichTextMenu="false"
                    :showAIComponents="showAIComponents"
                    class="email-richtext"
                    typeRichText="email"
                    :hash="hash"
                    @focus="isEditorFocused = true"
                    @blur="isEditorFocused = false"
                >
                    <template v-if="isDragging" #drag-handle>
                        <div
                            class="drag-text typographies-paragraph-large-bold"
                            @dragleave.stop="handleDragLeave"
                            @drop.prevent="handleDrop"
                        >
                            {{ labelDrag }}
                        </div>
                    </template>
                </RichText>

                <template v-if="expanded && !forward && !sending_type">
                    <CompassTooltip
                        :content="$t.__('email.EmailConsultPanelEditor.display_conversation')"
                        placement="right"
                    >
                        <div class="conversation-action" @click="expandReplyText">
                            <span v-for="dot in 3" :key="dot" class="dot"></span>
                        </div>
                    </CompassTooltip>
                </template>

                <template v-if="attachments.length">
                    <div
                        v-for="(item, index) in attachments"
                        :key="index"
                        class="attachments-wrapper"
                    >
                        <EmailAttachments
                            canBeDownloaded
                            :attachment="item"
                            @removeItem="removeAttachment"
                        />
                    </div>
                </template>

                <RichTextMenu
                    v-if="richTextEditor"
                    :editorMenu="richTextEditor"
                    :options="optionsRichText"
                    typeRichText="email"
                    :disabled="isPanelMinimized"
                    class="email-richtext-menu"
                    :hash="hashPanel"
                >
                    <template #actions>
                        <EmailCustomTemplateToolbar
                            v-if="!isReceivedTable && !isSentTable"
                            :hashPanel="hashPanel"
                            typePanel="consultPanel"
                        />
                    </template>
                </RichTextMenu>
            </div>
        </div>
        <div class="email-consult-editor__footer">
            <div
                v-if="showHint"
                class="email-consult-editor__ai-hint typographies-paragraph-small-regular"
                v-html="$t.__('richtext.ai.hint')"
            />
            <div class="email-consult-editor__actions">
                <CompassButton
                    :label="$t.__('common.actions.delete')"
                    type="secondary"
                    @click="handleDelete"
                />
                <template v-if="isFeatureScheduleEmailEnabled">
                    <CompassSplitButton
                        ref="SplitSendButton"
                        :label="$t.__('common.actions.send')"
                        :loading="isLoading"
                        @clickOnLabel="sendEmail"
                        @clickOnArrow="showDropdown = !showDropdown"
                    />
                    <CompassDropdown
                        v-model:isVisible="showDropdown"
                        parentRef="SplitSendButton"
                        placement="top-end"
                        dropdownClass="email-panel__schedule-dropdown"
                        @update:isVisible="(visibility) => handleVisibleChange(visibility)"
                        @itemSelected="openScheduleEmail"
                    >
                        <CompassDropdownOption
                            :label="$t.__('email.ScheduleEmailDialog.title')"
                            value="scheduleAction"
                            icon="calendar-reschedule"
                            :isCheckable="false"
                            @itemSelected="openScheduleEmail"
                        />
                    </CompassDropdown>
                    <ScheduleEmailDialog
                        :visible="showScheduleEmailDialog"
                        @scheduled="handleSendScheduledEmail"
                        @close="showScheduleEmailDialog = false"
                    />
                </template>
                <CompassButton
                    v-else
                    :label="$t.__('common.actions.send')"
                    :loading="isLoading"
                    @click="sendEmail"
                />
            </div>
        </div>
        <CompassModal
            v-model:visible="shouldDisplayErrorDialog"
            size="small"
            :title="$t.__('email.emailPanels.EmailPanel.error')"
            :primaryAction="primaryAction"
            :secondaryActions="secondaryActions"
        >
            <div class="typographies-paragraph-medium-regular">
                {{ textDialogError }}
            </div>
        </CompassModal>
        <CompassModal
            v-model:visible="shouldDisplayEmptyRecipientsWarningDialog"
            :title="$t.__('email.emailPanels.EmailPanel.empty_recipients_warning_dialog.title')"
            size="small"
            :primaryAction="primaryActionEmptyRecipientsWarningDialog"
            :secondaryActions="secondaryActionsEmptyRecipientsWarningDialog"
        >
            <p
                class="typographies-paragraph-medium-regular"
                v-html="emptyRecipientsWarningDialogText"
            />
        </CompassModal>
    </div>
</template>

<script>
import AvatarMixins from 'mixins/AvatarMixins';
import EmailsListingMixins from 'mixins/emails/EmailsListingMixins';
import { createNamespacedHelpers } from 'vuex';
import EmailRecipientsInputs from 'vcomponents/email/emailPanels/EmailRecipientsInputs.vue';
import EmailRelatedObject from 'vcomponents/email/EmailRelatedObject.vue';
import RichText from 'vcomponents/richtext/RichText.vue';
import RichTextMenu from 'vcomponents/richtext/RichTextMenu.vue';
const { mapGetters } = createNamespacedHelpers('user');
import { sendEmail } from 'api/emailEngine/emailEngineService';
import Feature from 'Services/Feature';
import generateHash from 'Helpers/hashGenerator.js';
import EmailAttachments from 'vcomponents/email/emailPanels/EmailAttachments.vue';
import EmailCustomTemplateToolbar from 'vcomponents/email/emailCustomTemplate/EmailCustomTemplateToolbar.vue';
import ScheduleEmailDialog from 'vcomponents/email/emailPanels/ScheduleEmailDialog.vue';
import { isMarginEnabled } from 'Helpers/richTextHelper.js';
import EmailEditorMixins from 'mixins/emails/EmailEditorMixins';
import EmailSubjectInput from 'vcomponents/email/emailPanels/EmailSubjectInput.vue';
import RichTextAttachmentsMixin from 'mixins/RichTextAttachmentsMixin';

export default {
    components: {
        EmailSubjectInput,
        EmailRecipientsInputs,
        EmailRelatedObject,
        RichTextMenu,
        RichText,
        EmailAttachments,
        EmailCustomTemplateToolbar,
        ScheduleEmailDialog,
    },
    mixins: [AvatarMixins, EmailsListingMixins, EmailEditorMixins, RichTextAttachmentsMixin],
    props: {
        prefillAttachments: {
            type: Array,
            default: () => [],
        },
        recipients: {
            type: Object,
            required: true,
        },
        replyType: {
            type: String,
            required: true,
        },
        hashPanel: {
            type: String,
            required: true,
        },
        thread: {
            type: Array,
            required: true,
        },
        replyContent: {
            type: String,
            default: '',
        },
        forward: {
            type: Boolean,
            default: false,
        },
        threadId: {
            type: String,
            default: '',
        },
        signature: {
            type: String,
            default: '',
        },
        prefillContent: {
            type: String,
            default: '',
        },
        sending_type: {
            type: String,
            default: '',
        },
        objectid: {
            type: String,
            default: '',
        },
        providerThreadId: {
            type: [Number, String],
            default: null,
        },
        isDraft: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['change', 'handleClose', 'delete'],
    data() {
        return {
            isUpdatingRecipients: false,
            currentReplyType: '',
            isPanelMinimized: false,
            richTextEditor: null,
            isLoading: false,
            expanded: true,
            attachments: [],
            email: {
                subject: '',
                addresses: [],
                content: '',
            },
            relatedObject: {
                type: 'document',
                name: 'Nom de mon objet lié à mon super email',
            },
            objectTypeMapper: {
                document: 'document',
                third: 'company',
                people: 'contact',
                opportunity: 'opportunity',
                company: 'company',
                contact: 'contact',
                individual: 'individual',
            },
            optionsRichText: [
                'BlockquoteTiptap',
                'SellsyAI',
                'Heading',
                'Bold',
                'Italic',
                'Underline',
                'Color',
                'Highlight',
                'BulletList',
                'OrderedList',
                'TextAlign',
                'Divider',
                'Link',
                'ImageResizable',
                'Attachment',
                'HeadingTiptap',
                'SizeTiptap',
            ],
            hash: generateHash(),
            labelDrag: this.$t.__('email.EmailConsultPanelEditor.label_drag'),
            isDragging: false,
            labelSucessEmailSend: this.$t.__('email.common.label_sucess_email_send'),
            labelSuccessScheduledEmailSend: this.$t.__(
                'email.common.label_sucess_scheduled_email_send',
            ),
            isEditorFocused: false,
            showDropdown: false,
            showScheduleEmailDialog: false,
        };
    },
    computed: {
        ...mapGetters(['getUser']),
        showAIComponents() {
            return Feature.isEnabled('sellsy-ai');
        },
        showHint() {
            return this.isEditorFocused && isMarginEnabled();
        },
        currentUserFullName() {
            return `${this.getUser.forename} ${this.getUser.name}`;
        },
        currentUserInitials() {
            return `${this.getUser.forename.charAt(0)}${this.getUser.name.charAt(0)}`;
        },
        isParticular() {
            return (
                ['client', 'prospect', 'supplier'].includes(this.identity && this.identity.type) &&
                !this.identity.isCorporation
            );
        },
        identity() {
            return this.$store.getters.identity;
        },
        isLinkedObjectExist() {
            return this.thread.length && this.thread[0].linked_object;
        },
        isFeatureScheduleEmailEnabled() {
            return Feature.isEnabled('email-engine-scheduled-emails');
        },
    },
    watch: {
        email: {
            handler() {
                this.$emit('change', { email: this.email, attachments: this.attachments });
            },
            deep: true,
        },
        attachments: {
            handler() {
                this.$emit('change', { email: this.email, attachments: this.attachments });
            },
            deep: true,
        },
    },
    mounted() {
        this.richTextEditor = this.$refs.refEmailPanelRichText.editor;
        this.richTextEditor
            .chain()
            .insertContent('<p></p><p></p>')
            .insertCustomSignature({
                signature: this.signature,
            })
            .run();

        const lastEmail = this.thread[0];

        if (this.forward) {
            this.email.subject = 'FWD: ' + lastEmail.subject;
        } else {
            this.email.subject = lastEmail.subject.includes('RE:')
                ? lastEmail.subject
                : 'RE: ' + lastEmail.subject;
        }

        if (this.isDraft) {
            this.attachments = this.prefillAttachments;
            this.email.content = this.replyContent;
        } else {
            if (this.sending_type) {
                this.attachments = this.prefillAttachments;
                this.email.content = this.prefillContent;
            }
            if (this.forward) {
                this.attachments = this.thread[0].attachments;
                this.email.content += this.replyContent;
            }
        }

        this.$nextTick(() => {
            this.richTextEditor.commands.focus('start');
        });
    },
    created() {
        EventBus.on(`richText-${this.hashPanel}:addAttachment`, (file) => {
            this.updateAttachmentList(file);
        });
        EventBus.on(`richText-${this.hashPanel}:loadingAttachments`, (fileInfo) => {
            this.attachments.push(fileInfo);
        });
    },
    beforeUnmount() {
        EventBus.off(`richText-${this.hashPanel}:addAttachment`);
        EventBus.off(`richText-${this.hashPanel}:loadingAttachments`);
    },
    methods: {
        handleDragOver() {
            this.isDragging = true;
            this.richTextEditor.setEditable(false);
        },
        handleDragLeave() {
            this.isDragging = false;
            this.richTextEditor.setEditable(true);
        },
        async handleDrop(event) {
            this.isDragging = false;
            this.richTextEditor.setEditable(true);
            const files = event.dataTransfer.files;

            for (const file of files) {
                const formData = new FormData();
                formData.append('file', file);
                const formattedFile = formData;
                let fileDetails = {};

                if (file) {
                    fileDetails = {
                        name: file.name,
                        size: file.size,
                        content_type: file.type,
                        content_id: file.name,
                        extension: '',
                        isFileLoaded: true,
                    };
                    await EventBus.emit(
                        `richText-${this.hashPanel}:loadingAttachments`,
                        fileDetails,
                    );
                }

                ApiClient.post('/email/attachment', {}, formattedFile)
                    .then((response) => {
                        if (response.data) {
                            const fileInfo = response.data;
                            const parsedFile = {
                                ...fileInfo,
                                content_disposition: 'attachment',
                            };
                            EventBus.emit(`richText-${this.hashPanel}:addAttachment`, parsedFile);
                        }
                    })
                    .catch((error) => {
                        const errorMessage = error.response.data.error.message;
                        const errorResponse = {
                            name: fileDetails.name,
                            size: fileDetails.size,
                            error_text:
                                typeof errorMessage === 'object'
                                    ? Object.values(errorMessage)[0]
                                    : errorMessage,
                            error: true,
                            error_message:
                                typeof errorMessage === 'object'
                                    ? Object.values(errorMessage)[0]
                                    : errorMessage,
                        };
                        EventBus.emit(`richText-${this.hashPanel}:addAttachment`, errorResponse);
                    });
            }
        },
        expandReplyText() {
            this.expanded = false;
            this.email.content += this.replyContent;
        },
        onUpdateAddressesEmails(addresses) {
            this.email.addresses = addresses;
        },
        handleDelete() {
            this.$emit('delete');
        },
        getRelatedObject() {
            const id = parseInt(this.$route.params.id);
            const objectType = this.isParticular ? 'individual' : this.$route.name?.split('.')[0];
            if (objectType && this.objectTypeMapper[objectType] && id > 0) {
                return [{ id: id, type: this.objectTypeMapper[objectType] }];
            }
            return [];
        },
        async sendEmail() {
            const recipientsTo = this.getRecipentsByType('to');
            const recipientsCC = this.getRecipentsByType('cc');
            const recipientsBCC = this.getRecipentsByType('bcc');
            const related = this.getRelatedObject();
            const emailContentWithtoutNodeSignature = this.email.content.replace(
                /<customsignature[\s\S]*?<\/customsignature>/g,
                this.signature,
            );

            const dataToSend = {
                subject: this.email.subject,
                content: emailContentWithtoutNodeSignature,
                in_reply_to_id: this.threadId,
                attachments: this.attachments,
                to: recipientsTo,
                cc: recipientsCC,
                bcc: recipientsBCC,
                related: related,
                sending_type: this.replyType,
            };

            if (!this.areRecipientsValid()) {
                return;
            }

            if (this.providerThreadId) {
                dataToSend.provider_thread_id = this.providerThreadId;
            }

            if (this.sending_type) {
                dataToSend.email_id = this.objectid;
            }

            if (this.scheduledDate) {
                dataToSend.scheduled_at = this.scheduledDate;
            }

            this.isLoading = true;

            await sendEmail(dataToSend)
                .then(() => {
                    const toastOptions = {
                        type: 'success',
                        icon: true,
                        text: this.labelSucessEmailSend,
                        close: true,
                    };
                    if (this.scheduledDate) {
                        toastOptions.text = this.labelSuccessScheduledEmailSend;
                        toastOptions.action = {
                            text: this.$t.__('common.labels.see'),
                            callback: () => {
                                this.$router.push({
                                    name: 'emails.scheduled',
                                });
                            },
                        };
                    }

                    this.$pushToast(toastOptions);

                    Feature.isEnabled('email-engine') &&
                        window.EventBus.emit('timeline:addMockedLog', dataToSend);
                    this.$emit('handleClose');
                })
                .catch((error) => {
                    if (error.status !== 402) {
                        this.$pushToast({
                            type: 'error',
                            icon: true,
                            text: error,
                            close: true,
                        });
                    }
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        handleVisibleChange(visibility) {
            if (!visibility && this.showDropdown) {
                this.showDropdown = false;
            }
        },
        openScheduleEmail() {
            this.showScheduleEmailDialog = true;
        },
        handleSendScheduledEmail(scheduledDate) {
            this.scheduledDate = scheduledDate;
            this.showScheduleEmailDialog = false;
            this.sendEmail();
        },
    },
};
</script>
<style lang="scss">
.email-richtext-menu {
    padding: var(--spacing-1);

    .menu {
        display: flex;
        gap: var(--spacing-0);
    }
}
.email-consult-editor {
    .email-related-object {
        margin: 0 var(--spacing-3) var(--spacing-2);
    }
}
</style>

<style lang="scss" scoped>
.rich-text {
    position: relative;
}

.drag-text {
    height: 100%;
    width: calc(100% - 32px);
    position: absolute;
    top: 0;
    left: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--colors-common-text-standard-default);
    border: 1px dashed var(--colors-common-border-focus);
    border-radius: var(--spacing-0);
    background-color: var(--colors-common-background-selected-hover);
    opacity: 0.9;
}

.email-consult-editor {
    padding: var(--spacing-3) 0 0;

    &__wrapper {
        display: flex;
        gap: var(--spacing-3);

        .compass-avatar {
            flex-shrink: 0;
        }
    }

    .attachments-wrapper {
        cursor: pointer;
        margin-left: var(--spacing-1);
        margin-bottom: var(--spacing-1);
    }

    &__panel {
        border: 1px solid var(--colors-common-border-default);
        border-radius: 4px;
        flex: 1 1 100%;

        .email-richtext:deep() {
            padding: 0 var(--spacing-3);
            position: relative;
            .tiptap.ProseMirror {
                min-height: 110px;
            }
        }

        .conversation-action {
            width: var(--spacing-5);
            height: var(--spacing-3);
            display: flex;
            justify-content: center;
            align-items: center;
            background-color: var(--colors-common-background-subtle);
            border: none;
            border-radius: var(--spacing-4);
            margin: var(--spacing-2) 0 var(--spacing-2) var(--spacing-3);
            padding: 0 6px;
            gap: var(--spacing-0);
            cursor: pointer;

            .dot {
                width: 4px;
                height: 4px;
                background-color: var(--colors-common-text-standard-caption);
                border-radius: 50%;
            }
        }

        &-header {
            display: flex;
            gap: var(--spacing-1);
            padding: calc(var(--spacing-1) - 1px) calc(var(--spacing-3) - 1px) 0;

            .compass-menu {
                border-bottom: 1px solid var(--colors-common-border-lighter);
                flex-shrink: 0;
                margin-bottom: var(--spacing-0);
            }

            .email-recipients-inputs {
                flex: 1 1 auto;
            }
        }

        &-subject {
            margin: 0 calc(var(--spacing-3) - 1px) var(--spacing-2);
        }
    }

    &__actions {
        display: flex;
        gap: var(--spacing-3);
        margin-left: auto;
    }

    &__ai-hint {
        color: var(--colors-common-text-subtle-default);
        padding-left: 48px;
    }

    &__footer {
        align-items: center;
        border-bottom: 1px solid var(--colors-common-border-default);
        display: flex;
        gap: var(--spacing-4);
        justify-content: space-between;
        padding: var(--spacing-3) 0 var(--spacing-5);
    }

    img {
        max-width: 100%;
        height: auto;
    }
}
</style>
<style>
.compass-menu__dropdown__container.email-send-options {
    z-index: 1000;
}
</style>
